<script lang="ts">
  import { Notas, type Nota } from '../lib/api';
  import { session } from '../lib/auth.svelte';
  import {
    EVAL_FISIO_TIPO,
    cargarPlantillas,
    evaluacionesDesdeNotas,
    guardarPlantillas,
    plantillaPorId,
    restaurarPlantillasDefault,
    resumenEvaluacion,
    serializeEvaluacion,
    validarEvaluacion,
    valoresVacios,
    type EvaluacionFisio,
    type PlantillaEvaluacion,
  } from '../lib/fisioterapia-evaluaciones';

  let {
    pacienteId,
    autor = 'Fisioterapista',
    notas = [],
    onSaved,
    compact = false,
  }: {
    pacienteId: string;
    autor?: string;
    notas?: Nota[];
    onSaved?: () => void | Promise<void>;
    compact?: boolean;
  } = $props();

  const tenantId = $derived(session.value?.tenantId ?? 'default');

  let plantillas = $state<PlantillaEvaluacion[]>([]);
  let plantillaId = $state('dolor');
  let valores = $state<Record<string, string | number | null>>({});
  let observaciones = $state('');
  let error = $state<string | null>(null);
  let guardando = $state(false);
  let editarPlantillas = $state(false);
  let plantillaEdit = $state<PlantillaEvaluacion | null>(null);

  const plantillaActiva = $derived(plantillaPorId(plantillas, plantillaId) ?? plantillas[0]);
  const historial = $derived(evaluacionesDesdeNotas(notas));

  $effect(() => {
    plantillas = cargarPlantillas(tenantId);
  });

  $effect(() => {
    if (plantillaActiva) valores = valoresVacios(plantillaActiva);
  });

  function seleccionarPlantilla(id: string) {
    plantillaId = id;
    observaciones = '';
    error = null;
  }

  async function registrar(e: SubmitEvent) {
    e.preventDefault();
    if (!pacienteId || !plantillaActiva) return;
    const ev: EvaluacionFisio = {
      plantillaId: plantillaActiva.id,
      plantillaNombre: plantillaActiva.nombre,
      autor,
      campos: { ...valores },
      observaciones,
    };
    const err = validarEvaluacion(ev, plantillaActiva);
    if (err) { error = err; return; }
    guardando = true;
    error = null;
    try {
      await Notas.crear({
        pacienteId,
        autor,
        tipo: EVAL_FISIO_TIPO,
        contenido: serializeEvaluacion(ev),
      });
      observaciones = '';
      valores = valoresVacios(plantillaActiva);
      await onSaved?.();
    } catch (ex: unknown) {
      error = ex instanceof Error ? ex.message : String(ex);
    } finally {
      guardando = false;
    }
  }

  function abrirEditor(p: PlantillaEvaluacion) {
    plantillaEdit = structuredClone(p);
    editarPlantillas = true;
  }

  function guardarPlantillaEditada() {
    if (!plantillaEdit) return;
    plantillas = plantillas.map(p => p.id === plantillaEdit!.id ? plantillaEdit! : p);
    guardarPlantillas(tenantId, plantillas);
    editarPlantillas = false;
    plantillaEdit = null;
  }

  function restaurarDefaults() {
    plantillas = restaurarPlantillasDefault(tenantId);
    plantillaId = plantillas[0]?.id ?? 'dolor';
  }

  function agregarCampo() {
    if (!plantillaEdit) return;
    plantillaEdit.campos = [
      ...plantillaEdit.campos,
      { id: `campo_${Date.now()}`, etiqueta: 'Nuevo campo', tipo: 'text' },
    ];
  }

  function quitarCampo(id: string) {
    if (!plantillaEdit) return;
    plantillaEdit.campos = plantillaEdit.campos.filter(c => c.id !== id);
  }
</script>

<div class="fisio-eval" class:compact>
  <p class="intro">
    Elija el <strong>tipo de evaluación</strong> según el objetivo clínico. Las plantillas son
    <strong>editables</strong> para su clínica (dolor, edema, sensibilidad, fuerza, reflejos, ROM, etc.).
  </p>

  {#if error}<p class="err">⚠ {error}</p>{/if}

  <div class="plantillas-tabs">
    {#each plantillas as p (p.id)}
      <button
        type="button"
        class:active={plantillaId === p.id}
        onclick={() => seleccionarPlantilla(p.id)}
        title={p.objetivo}
      >
        {p.nombre.replace(/^Evaluación de /, '').replace(/^Evaluación /, '')}
      </button>
    {/each}
  </div>

  {#if plantillaActiva}
    <p class="objetivo">{plantillaActiva.objetivo}</p>
    <form class="form-eval" onsubmit={registrar}>
      <div class="campos-grid">
        {#each plantillaActiva.campos as campo (campo.id)}
          <label class="campo">
            <span>{campo.etiqueta}{campo.requerido ? ' *' : ''}{campo.unidad ? ` (${campo.unidad})` : ''}</span>
            {#if campo.tipo === 'select'}
              <select bind:value={valores[campo.id]}>
                <option value="">—</option>
                {#each campo.opciones ?? [] as op}
                  <option value={op}>{op}</option>
                {/each}
              </select>
            {:else if campo.tipo === 'textarea'}
              <textarea rows="2" placeholder={campo.placeholder} bind:value={valores[campo.id]}></textarea>
            {:else if campo.tipo === 'number'}
              <input
                type="number"
                min={campo.min}
                max={campo.max}
                step="any"
                placeholder={campo.placeholder}
                bind:value={valores[campo.id]}
              />
            {:else}
              <input type="text" placeholder={campo.placeholder} bind:value={valores[campo.id]} />
            {/if}
          </label>
        {/each}
      </div>
      <label class="campo full">
        <span>Observaciones adicionales</span>
        <textarea rows="2" placeholder="Hallazgos, comparación con sesión anterior…" bind:value={observaciones}></textarea>
      </label>
      <button type="submit" class="primary" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar evaluación'}</button>
    </form>
  {/if}

  <details class="personalizar">
    <summary>Personalizar plantillas de evaluación</summary>
    <p class="hint">Agregue, renombre o ajuste campos por tipo de evaluación. Los cambios aplican solo a su clínica en este navegador.</p>
    <ul class="lista-plantillas">
      {#each plantillas as p (p.id)}
        <li>
          <strong>{p.nombre}</strong> — {p.campos.length} campos
          <button type="button" class="linkish" onclick={() => abrirEditor(p)}>Editar</button>
        </li>
      {/each}
    </ul>
    <button type="button" class="secondary" onclick={restaurarDefaults}>Restaurar plantillas predeterminadas</button>
  </details>

  {#if editarPlantillas && plantillaEdit}
    <div class="editor-overlay" role="dialog">
      <div class="editor-card">
        <h3>Editar: {plantillaEdit.nombre}</h3>
        <label>Nombre <input bind:value={plantillaEdit.nombre} /></label>
        <label>Objetivo <input bind:value={plantillaEdit.objetivo} /></label>
        {#each plantillaEdit.campos as campo, i (campo.id)}
          <div class="campo-editor">
            <input bind:value={plantillaEdit.campos[i].etiqueta} placeholder="Etiqueta" />
            <select bind:value={plantillaEdit.campos[i].tipo}>
              <option value="text">Texto</option>
              <option value="number">Número</option>
              <option value="textarea">Texto largo</option>
              <option value="select">Lista</option>
            </select>
            <input bind:value={plantillaEdit.campos[i].unidad} placeholder="Unidad (opc.)" />
            <button type="button" class="linkish danger" onclick={() => quitarCampo(campo.id)}>Quitar</button>
          </div>
        {/each}
        <button type="button" onclick={agregarCampo}>+ Agregar campo</button>
        <div class="editor-actions">
          <button type="button" onclick={() => { editarPlantillas = false; plantillaEdit = null; }}>Cancelar</button>
          <button type="button" class="primary" onclick={guardarPlantillaEditada}>Guardar plantilla</button>
        </div>
      </div>
    </div>
  {/if}

  {#if !compact}
    <h3>Historial de evaluaciones</h3>
    <table>
      <thead>
        <tr><th>Fecha</th><th>Tipo</th><th>Resumen</th><th>Profesional</th></tr>
      </thead>
      <tbody>
        {#each historial as ev (ev.registradaEn + ev.plantillaId)}
          <tr>
            <td>{ev.registradaEn ? new Date(ev.registradaEn).toLocaleString('es-GT') : '—'}</td>
            <td>{ev.plantillaNombre}</td>
            <td>{resumenEvaluacion(ev, plantillaPorId(plantillas, ev.plantillaId))}</td>
            <td>{ev.autor}</td>
          </tr>
        {:else}
          <tr><td colspan="4" class="empty">Sin evaluaciones registradas.</td></tr>
        {/each}
      </tbody>
    </table>
  {/if}
</div>

<style>
  .intro { color: var(--text-muted, #555); font-size: 0.92rem; margin-bottom: 0.75rem; }
  .plantillas-tabs { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-bottom: 0.5rem; }
  .plantillas-tabs button {
    padding: 0.35rem 0.65rem; border-radius: 999px; border: 1px solid var(--border, #ccc);
    background: var(--bg-elevated, #fff); font-size: 0.78rem; cursor: pointer;
  }
  .plantillas-tabs button.active { background: var(--brand, #0d6efd); color: #fff; border-color: transparent; }
  .objetivo { font-size: 0.85rem; color: #666; margin: 0 0 0.75rem; font-style: italic; }
  .campos-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); gap: 0.5rem; }
  .campo { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.8rem; }
  .campo.full { grid-column: 1 / -1; margin-top: 0.5rem; }
  .campo input, .campo select, .campo textarea {
    padding: 0.45rem 0.55rem; border-radius: 8px; border: 1px solid var(--border, #ccc); font: inherit;
  }
  .form-eval { margin-bottom: 1rem; }
  .primary { margin-top: 0.75rem; background: var(--brand, #0d6efd); color: #fff; border: 0; padding: 0.5rem 1rem; border-radius: 8px; cursor: pointer; }
  .secondary { margin-top: 0.5rem; padding: 0.4rem 0.8rem; border-radius: 8px; border: 1px solid #888; background: #fff; cursor: pointer; }
  .personalizar { margin: 1rem 0; font-size: 0.88rem; }
  .hint { color: #888; font-size: 0.82rem; }
  .lista-plantillas { list-style: none; padding: 0; margin: 0.5rem 0; }
  .lista-plantillas li { padding: 0.25rem 0; }
  .linkish { background: none; border: 0; color: var(--brand, #0d6efd); cursor: pointer; text-decoration: underline; font: inherit; }
  .linkish.danger { color: #b00020; }
  table { width: 100%; border-collapse: collapse; margin-top: 0.5rem; }
  th, td { padding: 0.4rem 0.55rem; border-bottom: 1px solid #eee; text-align: left; font-size: 0.85rem; }
  .empty { text-align: center; color: #888; font-style: italic; }
  .err { color: #b00020; }
  .editor-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.35); display: flex; align-items: center; justify-content: center; z-index: 100;
  }
  .editor-card {
    background: #fff; padding: 1.25rem; border-radius: 12px; max-width: 520px; width: 92%; max-height: 85vh; overflow: auto;
    display: flex; flex-direction: column; gap: 0.5rem;
  }
  .campo-editor { display: grid; grid-template-columns: 1fr auto auto auto; gap: 0.35rem; align-items: center; }
  .editor-actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.5rem; }
  .compact .intro { display: none; }
  .compact .personalizar { display: none; }
</style>
